/* LEGEND — order overlay (pills, drag-to-reprice, click-to-order popover). Ported from overlay.jsx. */
import React from 'react';
import { inProfit, pnl, fmtQty, fmtPx4, fmtPct } from '../lib/format';
import { BUCKETS, bucketShares } from '../lib/constants';
import { entryChipLabel } from '../lib/mappers';

const { useRef: useRefO, useEffect: useEffectO, useState: useStateO } = React;

const IconLock = () => React.createElement('svg', { className: 'lock', viewBox: '0 0 24 24', fill: 'none' },
  React.createElement('rect', { x: 5, y: 11, width: 14, height: 9, rx: 2, fill: 'currentColor' }),
  React.createElement('path', { d: 'M8 11V8a4 4 0 0 1 8 0v3', stroke: 'currentColor', strokeWidth: 2, fill: 'none' }));

const IconX = () => React.createElement('svg', { viewBox: '0 0 24 24', fill: 'none', stroke: 'currentColor', strokeWidth: 2.6 },
  React.createElement('path', { d: 'M6 6l12 12M18 6L6 18' }));

function Grip() {
  return React.createElement('div', { className: 'grip-rows' },
    React.createElement('i'), React.createElement('i'), React.createElement('i'));
}

export function OrderOverlay(props: any) {
  const { chartApiRef, position, lastPrice, colors, hover, popover, sizeParams,
    onOrderDrag, onOrderCommit, onPlaceOrder, onDismissPopover, onReposition, dragViz, onCancelOrder, side } = props;
  const overlayRef = useRefO<any>(null);
  const [drag, setDrag] = useStateO<any>(null);
  const dragRef = useRefO<any>(null); dragRef.current = drag;
  const onCancel = onCancelOrder || (() => {});
  const cancelBtn = (kind: string, id: string) => React.createElement('button', {
    className: 'pill-x', key: 'x', title: 'Cancel order',
    onPointerDown: (e: any) => { e.stopPropagation(); },
    onClick: (e: any) => { e.stopPropagation(); onCancel(kind, id); },
  }, IconX());

  useEffectO(() => {
    let raf: number;
    const loop = () => {
      const api = chartApiRef.current;
      const root = overlayRef.current;
      if (api && root) {
        const els = root.querySelectorAll('.ord[data-px]');
        els.forEach((el: any) => {
          if (dragRef.current && el.getAttribute('data-oid') === dragRef.current.id) return;
          const px = parseFloat(el.getAttribute('data-px'));
          const y = api.priceToY(px);
          if (y == null) { el.style.opacity = '0'; return; }
          el.style.opacity = '1';
          el.style.transform = `translateY(${y}px)`;
        });
      }
      raf = requestAnimationFrame(loop);
    };
    raf = requestAnimationFrame(loop);
    return () => cancelAnimationFrame(raf);
  }, []);

  function startDrag(e: any, kind: string, id: string, prec: number) {
    e.preventDefault(); e.stopPropagation();
    setDrag({ kind, id });
    const el = e.currentTarget.closest('.ord');
    el.classList.add('dragging');
    const move = (ev: any) => {
      const api = chartApiRef.current; if (!api) return;
      const rect = overlayRef.current.getBoundingClientRect();
      const y = ev.clientY - rect.top;
      let px = api.yToPrice(y);
      if (px == null) return;
      px = +px.toFixed(prec);
      el.style.transform = `translateY(${y}px)`;
      onOrderDrag(kind, id, px);
    };
    const up = (ev: any) => {
      window.removeEventListener('pointermove', move);
      window.removeEventListener('pointerup', up);
      el.classList.remove('dragging');
      setDrag(null);
      const api = chartApiRef.current;
      const rect = overlayRef.current.getBoundingClientRect();
      let px = api ? api.yToPrice(ev.clientY - rect.top) : null;
      if (px != null) { px = +px.toFixed(prec); onOrderCommit(kind, id, px); }
    };
    window.addEventListener('pointermove', move);
    window.addEventListener('pointerup', up);
  }

  const prec = (lastPrice != null && lastPrice < 1) ? 4 : 2;
  const els: any[] = [];

  if (position && position.qty > 0) {
    const up = inProfit(position.side, position.avg, lastPrice);
    const pl = pnl(position.side, position.avg, lastPrice, position.qty);
    els.push(
      React.createElement('div', {
        key: 'pos', className: 'ord ord-position ' + (up ? 'is-up' : 'is-down'),
        'data-px': position.avg, 'data-oid': 'pos',
        style: { color: up ? colors.up : colors.down },
      },
        React.createElement('div', { className: 'ord-line' }),
        React.createElement('div', { className: 'ord-pill', title: 'Average entry — locked' },
          React.createElement('div', { className: 'seg-a' }, IconLock(), React.createElement('span', null, fmtQty(position.qty))),
          React.createElement('div', { className: 'seg-b' },
            React.createElement('span', null, (pl.dollar >= 0 ? '+$' : '−$') + Math.abs(pl.dollar).toLocaleString('en-US', { maximumFractionDigits: 0 })),
            React.createElement('span', { className: 'sub' }, fmtPct(pl.pct))))));
  }

  if (position && position.targets) {
    position.targets.forEach((t: any) => {
      const dir = position.side === 'long' ? 1 : -1;
      let r: number | null = null;
      if (position.stop && position.avg) {
        const risk = Math.abs(position.avg - position.stop.px);
        if (risk > 0) r = Math.abs(t.px - position.avg) / risk * (((t.px - position.avg) * dir > 0) ? 1 : -1);
      }
      // Estimated PnL if this target fills (mirrors the stop's $ figure).
      const est = (t.px - position.avg) * dir * t.qty;
      els.push(
        React.createElement('div', { key: t.id, className: 'ord ord-target draggable' + (dragViz === t.id ? ' dragging' : ''), 'data-px': t.px, 'data-oid': t.id },
          React.createElement('div', { className: 'ord-line' }),
          dragViz === t.id ? React.createElement('span', { className: 'drag-cap' }, 'drag to reprice') : null,
          React.createElement('div', { className: 'ord-pill', onPointerDown: (e: any) => startDrag(e, 'target', t.id, prec) },
            React.createElement('div', { className: 'seg-a' }, React.createElement('span', { className: 'tag' }, t.t), Grip()),
            React.createElement('div', { className: 'seg-b' },
              React.createElement('span', null, fmtPx4(t.px)),
              React.createElement('span', { className: 'sub' }, '×' + fmtQty(t.qty)),
              React.createElement('span', { className: 'sub' }, (est >= 0 ? '+$' : '−$') + Math.abs(est).toLocaleString('en-US', { maximumFractionDigits: 0 })),
              r != null ? React.createElement('span', { className: 'sub' }, (r >= 0 ? '' : '−') + Math.abs(r).toFixed(1) + 'R') : null),
            cancelBtn('target', t.id))));
    });
  }

  if (position && position.stop && position.qty > 0) {
    const risk = (position.side === 'long' ? (position.avg - position.stop.px) : (position.stop.px - position.avg)) * position.stop.qty;
    els.push(
      React.createElement('div', { key: 'stop', className: 'ord ord-stop draggable' + (dragViz === position.stop.id ? ' dragging' : ''), 'data-px': position.stop.px, 'data-oid': position.stop.id },
        React.createElement('div', { className: 'ord-line' }),
        dragViz === position.stop.id ? React.createElement('span', { className: 'drag-cap' }, 'drag to reprice') : null,
        React.createElement('div', { className: 'ord-pill', onPointerDown: (e: any) => startDrag(e, 'stop', position.stop.id, prec) },
          React.createElement('div', { className: 'seg-a' }, React.createElement('span', { className: 'tag' }, position.trailing ? 'TRAIL' : 'STOP'), Grip()),
          React.createElement('div', { className: 'seg-b' },
            React.createElement('span', null, fmtPx4(position.stop.px)),
            React.createElement('span', { className: 'sub' }, (risk >= 0 ? '−$' : '+$') + Math.abs(risk).toLocaleString('en-US', { maximumFractionDigits: 0 }))),
          cancelBtn('stop', position.stop.id))));
  }

  if (position && position.entries) {
    position.entries.forEach((en: any) => {
      els.push(
        React.createElement('div', { key: en.id, className: 'ord ord-entry draggable', 'data-px': en.px, 'data-oid': en.id },
          React.createElement('div', { className: 'ord-line' }),
          React.createElement('div', { className: 'ord-pill' + (en.pending ? ' pending' : ''), onPointerDown: (e: any) => startDrag(e, 'entry', en.id, prec) },
            // 'dip'/'break' on the chip is GEOMETRY (below/above), so the label
            // has to be read through the side or a resting short above market
            // reads "BRK" — the long word for the opposite intent.
            React.createElement('div', { className: 'seg-a' }, React.createElement('span', { className: 'tag' },
              entryChipLabel(en.kind, side)), Grip()),
            React.createElement('div', { className: 'seg-b' },
              React.createElement('span', null, fmtPx4(en.px)),
              React.createElement('span', { className: 'sub' }, (en.bucketName ? en.bucketName + ' ' : '') + '×' + fmtQty(en.shares))),
            cancelBtn('entry', en.id))));
    });
  }

  let hintEl = null;
  if (hover && hover.price != null && lastPrice != null && !popover && !drag) {
    const isDip = hover.price < lastPrice;
    const hintCopy = ENTRY_COPY[side === 'short' ? 'short' : 'long']![isDip ? 'dip' : 'break']!;
    hintEl = React.createElement('div', { className: 'zone-hint show', style: { top: hover.y + 'px' } },
      React.createElement('div', { className: 'zh-line' }),
      React.createElement('div', { className: 'zh-label' }, hintCopy.kind),
      React.createElement('div', { className: 'zh-px mono' }, fmtPx4(+hover.price.toFixed(prec))));
  }

  return React.createElement('div', { className: 'overlay', ref: overlayRef },
    hintEl,
    els,
    popover ? React.createElement(OrderPopover, { popover, colors, sizeParams, onPlaceOrder, onDismiss: onDismissPopover, onReposition, prec, side }) : null);
}

// What a click at this price means, per side. 'dip' is BELOW market and 'break'
// is ABOVE — geometry, not direction — so only the wording changes here.
const ENTRY_COPY: Record<string, Record<string, { kind: string; blurb: string }>> = {
  long: {
    dip: { kind: 'Buy Dip', blurb: 'Resting buy below price. ' },
    break: { kind: 'Buy On Break', blurb: 'Stop-buy on a break above price. ' },
  },
  short: {
    dip: { kind: 'Short Breakdown', blurb: 'Stop-short on a break below price. ' },
    break: { kind: 'Short Pop', blurb: 'Resting short above price. ' },
  },
};

function OrderPopover({ popover, sizeParams, onPlaceOrder, onDismiss, onReposition, prec, side }: any) {
  const [sel, setSel] = useStateO<any>(null);
  const [placing, setPlacing] = useStateO(false);
  const isDip = popover.kind === 'dip';
  const copy = ENTRY_COPY[side === 'short' ? 'short' : 'long']![isDip ? 'dip' : 'break']!;
  useEffectO(() => {
    const onKey = (e: any) => { if (e.key === 'Escape') onDismiss(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  function pick(b: any) {
    setSel(b.key); setPlacing(true);
    setTimeout(() => { onPlaceOrder(popover.kind, +popover.price.toFixed(prec), b); }, 280);
  }

  const W = 268;
  let left = popover.x - W - 18;
  if (left < 12) left = popover.x + 18;
  const top = Math.max(8, Math.min(popover.stageH - 190, popover.y - 24));

  return React.createElement(React.Fragment, null,
    React.createElement('div', {
      className: 'overlay', style: { pointerEvents: 'auto', zIndex: 49, cursor: 'crosshair' },
      onClick: (e: any) => onReposition(e.clientX, e.clientY), onContextMenu: (e: any) => { e.preventDefault(); onDismiss(); },
    }),
    React.createElement('div', {
      className: 'order-pop ' + (isDip ? 'below' : 'above'),
      style: { left: left + 'px', top: top + 'px' }, onClick: (e: any) => e.stopPropagation(),
    },
      React.createElement('div', { className: 'op-head' },
        React.createElement('span', { className: 'op-dir' }, isDip ? 'Below' : 'Above'),
        React.createElement('span', { className: 'op-kind' }, copy.kind),
        React.createElement('span', { className: 'op-price mono' }, fmtPx4(+popover.price.toFixed(prec)))),
      React.createElement('div', { className: 'op-meta' },
        copy.blurb,
        React.createElement('b', null, 'Pick a size to send.')),
      React.createElement('div', { className: 'size-buckets' },
        BUCKETS.map((b) => React.createElement('button', {
          key: b.key, className: 'bucket' + (sel === b.key ? ' on' : ''), disabled: placing && sel !== b.key,
          onClick: () => pick(b),
        },
          React.createElement('span', { className: 'bk-name' }, b.name),
          React.createElement('span', { className: 'bk-sh mono' }, fmtQty(bucketShares(b.key, popover.price, sizeParams)))))),
      React.createElement('div', { className: 'op-foot' },
        placing
          ? React.createElement('span', { className: 'hint' }, 'Sending ' + (sel || '') + ' order…')
          : React.createElement(React.Fragment, null,
            React.createElement('span', { className: 'hint' }, 'Click a new level to move · '),
            React.createElement('kbd', null, 'Esc'),
            React.createElement('span', { className: 'hint' }, ' to cancel')))));
}
